import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import fr from 'facet-rest';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import WhatIfStepNavigator from 'Views/Page/Content/Modal/Components/WhatIfStepNavigator';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useNavigationContext } from 'Components/ComponentLibrary/Organisms/NavigationWithContext/NavigationContext';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import NewDialog from 'Components/UI/ConfirmationDialog/NewDialog';

const styles = (theme) => ({
  root: {
    width: '100%',
  },
  subtitle: {
    marginBottom: theme.spacing(4),
  },
  fields: {
    padding: theme.spacing(0),
  },
  content: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  badgeImage: {
    marginBottom: theme.spacing(4),
    width: '140px',
    height: '140px',
  },
  navigator: {
    maxWidth: theme.dimensions.md,
  },
  navigatorWBackButton: {
    maxWidth: 500,
  },
});

const Layout = ({
  className,
  classes,
  children,
  primaryActionLabel,
  onSubmitSuccess,
  onNext,
  onPrevious,
  submitConfirmation,
  submitConfirmationMessage,
}) => {
  const { refreshNavigationCollection, loadNavigationLink } = useNavigationContext();
  const { loadModalCollectionByLink } = useModalContext();

  const {
    resource,
    onSubmit,
    saving,
  } = useMetaLayoutContext();

  const isMobile = useMobileLayout();
  const shouldPostWithConfirmation = resource.meta().action('POST') && submitConfirmation;


  async function handleNext() {
    if (onNext) {
      onNext();
    } else {
      const resp = await onSubmit();

      let navigationLink;
      if (resp) {
        navigationLink = resp?.resources()
          .head()
          ?.relationship(fr.relationships.navigation.name)
          ?.link('related');
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      }

      if (navigationLink) {
        loadNavigationLink(navigationLink);
      } else {
        refreshNavigationCollection();
      }
    }
  }

  const {
    dialogOpen,
    handleSubmit,
    handleOpen,
    handleClose,
  } = useConfirmationDialogState({
    onSubmit: handleNext,
  });

  const handleOnNext = () => {
    if (shouldPostWithConfirmation) {
      return handleOpen;
    }
    return handleNext;
  };

  const previousLink = resource.relationship(fr.relationships.previousLink.name)?.link('related');
  const hasPrevious = !!previousLink || !!onPrevious;
  const handlePrevious = async () => {
    if (onPrevious) {
      onPrevious();
    }
    if (previousLink) {
      await loadModalCollectionByLink(previousLink.url());
      refreshNavigationCollection();
    }
    return null;
  };

  return (
    <Box
      className={classNames(classes.root, className)}
    >
      {submitConfirmation && dialogOpen && (
      <NewDialog
        title={'Are you sure?'}
        message={submitConfirmationMessage}
        cancelLabel={'Nevermind'}
        confirmLabel={'Continue'}
        confirmButtonType="error"
        loading={saving}
        onConfirm={async () => {
          await handleSubmit();
        }}
        onCancel={() => {
          handleClose();
        }}
        onClose={() => {
          handleClose();
        }}
        showCloseIcon={false}
      />
      )}
      <div className={classNames(classes.content, {
        [classes.contentDesktop]: !isMobile,
      })}
      >
        {children}
      </div>
      <WhatIfStepNavigator
        className={classNames(classes.navigator, { [classes.navigatorWBackButton]: !!previousLink })}
        isLoading={saving}
        onNext={handleOnNext()}
        onPrevious={hasPrevious && handlePrevious}
      >
        {primaryActionLabel || resource?.attr('primaryActionLabel')}
      </WhatIfStepNavigator>
    </Box>
  );
};

Layout.defaultProps = {
  className: '',
  children: undefined,
  primaryActionLabel: undefined,
  onSubmitSuccess: undefined,
  onNext: undefined,
  onPrevious: undefined,
  submitConfirmation: undefined,
  submitConfirmationMessage: undefined,
};

Layout.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  children: PropTypes.node,
  primaryActionLabel: PropTypes.string,
  onSubmitSuccess: PropTypes.func,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  submitConfirmation: PropTypes.bool,
  submitConfirmationMessage: PropTypes.string,
};

export default withStyles(styles)(Layout);
